import {
  AppAdminFormLabel,
  AppButton,
  AppCard,
  AppSelect,
  AppTextField,
} from '@/components/atoms';
import PresignedUploadField, {
  type UploadFieldValue,
} from './PresignedUploadField';
import { RA2NDatePicker } from './RA2NDatePicker';
import {
  insurerAttentionCreateSchema,
  type InsurerAttentionCreateFormData,
} from '@/schemas/insurerAttentionCreateSchema';
import { dateValueToIso } from '@/utils/dateConverter';
import useAppToast from '@/hooks/useAppToast';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { Flex } from 'antd';

interface Props {
  prefectureOptions: { value: number; label: string }[];
  isSubmitting: boolean;
  maxAttachments?: number;
  selectedCount: number;
  onAttachmentsChange: (slots: (UploadFieldValue | null)[]) => void;
  onSubmit: (data: InsurerAttentionCreateFormData) => void;
}

const AdminInsurerAttentionCreateForm = ({
  prefectureOptions,
  isSubmitting,
  maxAttachments = 5,
  selectedCount,
  onAttachmentsChange,
  onSubmit,
}: Props) => {
  const { error: toastError } = useAppToast();
  const [slots, setSlots] = useState<(UploadFieldValue | null)[]>(
    Array(maxAttachments).fill(null)
  );
  const [uploadingFlags, setUploadingFlags] = useState<boolean[]>(
    Array(maxAttachments).fill(false)
  );
  const [uploadInvalidFlags, setUploadInvalidFlags] = useState<boolean[]>(
    Array(maxAttachments).fill(false)
  );

  const {
    control,
    handleSubmit,
    clearErrors,
    formState: { errors },
  } = useTrimmedForm<InsurerAttentionCreateFormData>({
    resolver: zodResolver(insurerAttentionCreateSchema),
    defaultValues: { prefecture_id: '', publish_date: '', remarks: '' },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  useEffect(() => {
    onAttachmentsChange(slots);
  }, [slots, onAttachmentsChange]);

  const handleSlotChange = (index: number, value: UploadFieldValue | null) => {
    setSlots(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleSlotUploading = (index: number, uploading: boolean) => {
    setUploadingFlags(prev => {
      const next = [...prev];
      next[index] = uploading;
      return next;
    });
  };

  const handleSlotValidationError = (index: number, hasError: boolean) => {
    setUploadInvalidFlags(prev => {
      if (prev[index] === hasError) return prev;
      const next = [...prev];
      next[index] = hasError;
      return next;
    });
  };

  const isUploading = uploadingFlags.some(Boolean);
  const hasUploadValidationError = uploadInvalidFlags.some(Boolean);

  const handleFormSubmit = handleSubmit(onSubmit, () => {
    toastError('エラーが発生しました。');
  });

  const onClickSubmit = () => {
    if (selectedCount === 0) {
      toastError('追加するデータを選択してください。');
      return;
    }
    if (isUploading) {
      toastError('ファイルをアップロード中です。');
      return;
    }
    if (hasUploadValidationError) {
      toastError('エラーが発生しました。');
      return;
    }
    handleFormSubmit();
  };

  return (
    <AppCard title="新規追加情報">
      <div className="space-y-4">
        <div className="w-1/3">
          <AppAdminFormLabel label="都道府県" required />
          <Controller
            name="prefecture_id"
            control={control}
            render={({ field }) => (
              <AppSelect
                placeholder=""
                options={prefectureOptions}
                className="w-full"
                value={field.value ? Number(field.value) : undefined}
                onChange={val => {
                  field.onChange(val ? val.toString() : '');
                  clearErrors('prefecture_id');
                }}
                status={errors.prefecture_id ? 'error' : undefined}
              />
            )}
          />
          {errors.prefecture_id && (
            <p className="text-red-500 text-small mt-1">{errors.prefecture_id.message}</p>
          )}
        </div>

        <div className="w-1/3">
          <AppAdminFormLabel label="掲載日" required />
          <Controller
            name="publish_date"
            control={control}
            render={({ field }) => (
              <RA2NDatePicker
                value={field.value || null}
                onChange={value => {
                  field.onChange(dateValueToIso(value) ?? '');
                  clearErrors('publish_date');
                }}
                error={!!errors.publish_date}
                placeholder=""
                className="w-full"
              />
            )}
          />
          {errors.publish_date && (
            <p className="text-red-500 text-small mt-1">{errors.publish_date.message}</p>
          )}
        </div>

        <div>
          <AppAdminFormLabel label="注意事項" required />
          <Controller
            name="remarks"
            control={control}
            render={({ field }) => (
              <AppTextField
                value={field.value}
                onChange={e => {
                  field.onChange(e.target.value);
                  clearErrors('remarks');
                }}
                status={errors.remarks ? 'error' : undefined}
              />
            )}
          />
          {errors.remarks && (
            <p className="text-red-500 text-small mt-1">{errors.remarks.message}</p>
          )}
        </div>

        <div>
          <AppAdminFormLabel label="資料" />
          <div className="space-y-2">
            {Array.from({ length: maxAttachments }, (_, i) => (
              <PresignedUploadField
                key={i}
                context="insurer_attention_attachment"
                value={slots[i] ?? null}
                onChange={val => handleSlotChange(i, val)}
                onUploadingChange={flag => handleSlotUploading(i, flag)}
                onValidationErrorChange={hasError => handleSlotValidationError(i, hasError)}
                className="border rounded px-3 py-2"
              />
            ))}
          </div>
        </div>

        <Flex justify="end">
          <AppButton
            type="primary"
            onClick={onClickSubmit}
            loading={isSubmitting}
            disabled={isUploading}
          >
            一括登録
          </AppButton>
        </Flex>
      </div>
    </AppCard>
  );
};

export default AdminInsurerAttentionCreateForm;
